import React, { useEffect, useState, forwardRef, useMemo, useCallback } from 'react';
import _, { isEmpty, uniqBy } from 'lodash';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes, parseSelectOptions, generateFormFields } from 'utils/formUtils';
import { Form } from 'antd';

const JfrogXrayMappingForm = forwardRef(({disableProjectFields = false, ...props}, ref) => {
  const [projects, setProjects] = useState(() => {
    if (props.record && props.record.projectName) {
      const { projectKey, projectName } = props.record;
      return [{id: projectKey || projectName, name: projectName}]
    }
    return [];
  });

  const [builds, setBuilds] = useState(() => {
    if (props.record && props.record.buildName) {
      const { buildName, buildRepo } = props.record;
      const buildNameArrays = buildName.split(',');
      const buildRepoArrays = buildRepo ? buildRepo.split(',') : null;
  
      return buildNameArrays.map((id, index) => ({
        id,
        name: id,
        otherProperties: buildRepoArrays
          ? {
              type: 'build',
              buildRepo: buildRepoArrays[index] || null
            }
          : null
      }));
    }
    return [];
  });

  const [selectedProject, setSelectedProject] = useState("");
  const [repos, setRepos] = useState({});
 
  const [form] = Form.useForm();

  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [
      {
        fetchLatestArtifactReport: true,
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
        configType: 'projects',
        projectList: [{ projectKey: undefined }],
      },
    ];
  });

  const fetchProjects = useCallback((configType, params) => {
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, ...(configType && { type : configType }), ...params });
    promise.then((response) => {
      const { projects } = response;
      if(configType === 'builds') {
        setBuilds((prevBuilds) => uniqBy([...prevBuilds, ...projects], 'id'));
      } else {
        setProjects(projects);
      }
    }).catch(() => {
      message.error('Problem fetching projects');
    });
    return { promise, cancel };
  },[props.loginConfigId,props.toolName]);

  useEffect(() => {
    const { cancel } = fetchProjects();
    return () => {
      cancel && cancel();
    };
  },[fetchProjects]);

  const fetchBuilds = useCallback((params) => {
      return fetchProjects("builds", params)
  }, [fetchProjects]);

  const getExtraFieldsData = (config) => {
    const { projectRepos, projectKey, dateRange, fetchLatestArtifactReport,buildName, configType } = config;
    let extraFieldData = { configType };
    if(configType === 'builds') {
      const data = builds.reduce((acc, cv) => {
        const { id, name, otherProperties } = cv;
        const buildRepo = otherProperties?.buildRepo;
      
        if (buildName.includes(id)) {
          acc.buildId.push(id);
          acc.buildName.push(name);
          acc.buildRepo.push(buildRepo);
        }
        return acc;
      }, { buildId: [], buildName: [], buildRepo: [] });
      
      extraFieldData = {
        ...extraFieldData,
        buildName: data.buildName.join(),
        buildRepo: _.compact(data.buildRepo).join(),
      };
    } else {
      const name = projects.find(p => projectKey === p.id)?.name;
      extraFieldData = {
        ...extraFieldData,
        projectName: name,
        projectRepos: _.isEmpty(projectRepos) ? null : projectRepos.join(),
        projectKey,
        dateRange,
        fetchLatestArtifactReport
      }
    }
    return extraFieldData
  };

  const fetchRepos = useCallback((selectedProject) => {
    if(!repos[selectedProject] && selectedProject){
      const {promise,cancel} = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId, name: selectedProject });
      promise.then(res => {
        const { projects } = res
        const repo = projects[0]?.otherProperties?.projectRepos;
        setRepos(repoList => ({...repoList, [selectedProject]: (repo || []).map(i => ({label: i, value: i}))}))
      }).catch(()=> message.error('Problem fetching project repositories'));
      return cancel;   
    }
  }, [props.toolName, props.loginConfigId, repos]);

  useEffect(() => fetchRepos(selectedProject), [fetchRepos, selectedProject]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { projectKey, projectRepos, fetchLatestArtifactReport, buildName } = config;
    if(projectKey){
      setSelectedProject(projectKey);
    }
    return { 
      ...(!isEmpty(buildName) && {buildName: buildName.split(',')}),
      ...(!isEmpty(projectRepos) && {projectRepos: projectRepos.split(',')}),
      fetchLatestArtifactReport:  fetchLatestArtifactReport === 'true' ? true : false,
      configType: buildName ? 'builds' : 'projects'
    };
  }, []);

  const isProjectFieldDisabled = useCallback((fieldKey, form) => {
    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
    return (!fieldConfiguration || !fieldConfiguration.projectKey);
  }, []);

  const projectListRender = useCallback((listField) => {
    const selectedType = form.getFieldsValue().configurations[listField.name]?.configType;
    const projectFields =  [
      {
        name: 'projectKey',
        colSpan: 10,
        type: FieldTypes.SELECT,
        label: 'Jfrog Xray Project',
        options: parseSelectOptions(projects),
        disabled: disableProjectFields,
        rules: [{ required: true, message: `Jfrog Xray Project is required` }],
        props: {
          autoFocus: true,
          onChange: (val, field) => {
            setSelectedProject(val);
            const { name } = field;
            const configurations = form.getFieldValue("configurations");
            configurations[name] = {
              ...configurations[name],
              projectRepos: undefined
            }
            form.setFieldsValue({ configurations: configurations });
          }
        },
      },
      {
        name: 'projectRepos',
        colSpan: 10,
        type: FieldTypes.MULTI_SELECT,
        label: 'Project Repositories',
        propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form) || disableProjectFields }),
        props: {
          getFormListOptions: (fieldKey) => {
            const configs = form.getFieldValue("configurations");
            const project = configs[fieldKey]?.projectKey;
            return repos[project]
          },
        }
      },
      {
        name: 'dateRange',
        colSpan: 10,
        type: FieldTypes.NUMBER,
        min: 1,
        disabled: disableProjectFields,
        rules: [{
        validator: (rule, value) => {
            if (value && value < 1) {
                  return Promise.reject("Days cannot be less than 1.");
                }
              return Promise.resolve();
          }
        }],
        label: 'Historical Data Range (in Days)',
        help: 'This config specifies historical data range. If config is specified then data for the past specific days will be pulled,  else ArmorCode will pull all the data from Jfrog Xray.'
      },
      {
        name: 'fetchLatestArtifactReport',
        colSpan: 24,
        type: FieldTypes.BOOLEAN,
        label: 'Fetch Latest Artifact Report',
        help: 'Retrieves the scan report for the most recent artifact from each selected repository. If unchecked, ArmorCode will ingest reports for all artifacts.',
        inline: true
      },
    ]

    const buildFields = [
      {
        name: 'buildName',
        colSpan: 10,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: 'Build Name',
        props: {
          api: fetchBuilds,
          allowClear: true,
        },
        disabled: disableProjectFields,
        required: true,
      }
    ]
    const formFields = selectedType === 'builds' ? buildFields : projectFields;
    return (<div className="m-b-sm">
      {generateFormFields(formFields, 10, listField)}
    </div>)

  },[disableProjectFields,isProjectFieldDisabled, form, projects, repos, fetchBuilds]);

  const extraFields = useMemo(() =>
    [
      {
        name: "configType",
        type: FieldTypes.RADIO,
        options: [{ label: 'Projects', value: 'projects' }, { label: 'Builds', value: 'builds' }],
        disabled: disableProjectFields
      },
      {
        name: (listField) => [listField.name, "projectList"],
        render: projectListRender
      }
    ],
    [projectListRender, disableProjectFields])


  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      newRowDefaultValue={initialValues[0]}
      {...props}
    />
  )
})

export default JfrogXrayMappingForm;